import {
    announcementsBottomNewsContainerElement,
    announcementsInformationPreviewElement,
    announcementsInformationRestElement,
    moreBottomAnnouncementsButtonNewsContainerElement,
    newsBottomRowMeldungen,
} from "@modules/global/dom.ts"

import { scrollPageTo } from "@modules/advert-cards/home.ts"
import { setUrlParamsToSpecificValue } from "@modules/news/urlParams/url-param.ts"

const announcementsStorageKey = "openBottomAnnouncements"
const bottomAnnouncementRestLoadedStorageKey = "bottomAnnouncementsRestLoaded"

let gutter = 19
if (SiteInfo.isMobileVersion) {
    gutter = 10
}

const announcementsSection = announcementsBottomNewsContainerElement()
const announcementsButton = moreBottomAnnouncementsButtonNewsContainerElement()
const announcementsPreview = announcementsInformationPreviewElement()
const announcementsRest = announcementsInformationRestElement()
const announcementsContainer = newsBottomRowMeldungen() as HTMLElement

const calculateAndSetHeights = () => {
    const announcementsSection = announcementsBottomNewsContainerElement()
    if (!announcementsSection) return

    const announcementsContainer = newsBottomRowMeldungen() as HTMLElement
    const card = announcementsSection.querySelector<HTMLDivElement>(".anzeige-meldung")
    const title = announcementsSection.querySelector<HTMLDivElement>(".stripe-title")
    const moreCardsSection = document.querySelector<HTMLDivElement>("#bottom-meldungen .more-content.row.section-1")

    if (!announcementsContainer || !card || !title) return

    const isOpen = sessionStorage.getItem(announcementsStorageKey) === "1"

    announcementsContainer.style.height = "auto"

    const maxHeight = announcementsContainer.offsetHeight

    announcementsContainer.dataset.maxHeight = maxHeight.toString()

    const cardHeight = card.clientHeight
    const titleHeight = title.clientHeight
    let minHeight = cardHeight + cardHeight + titleHeight + gutter

    if (SiteInfo.isMobileVersion) {
        minHeight = 4 * cardHeight + titleHeight + gutter
    }

    if (!moreCardsSection) return

    if (isOpen) {
        announcementsContainer.style.height = `${maxHeight}px`
        announcementsSection.classList.add("more-is-open")
    } else {
        announcementsContainer.style.height = `${minHeight}px`
        announcementsSection.classList.remove("more-is-open")
    }
}

let announcementsTimeout: Timer | number = 0
const setTimeoutForCalculateAndSetHeights = () => {
    clearTimeout(announcementsTimeout)
    announcementsTimeout = setTimeout(calculateAndSetHeights, 50)
}

const showAnnouncements = () => {
    const announcementsSection = announcementsBottomNewsContainerElement()
    if (!announcementsSection) return

    const announcementsContainer = newsBottomRowMeldungen() as HTMLElement
    if (!announcementsContainer) return

    const maxHeight = Number.parseInt(announcementsContainer.dataset.maxHeight ?? "0")
    if (maxHeight === 0) return

    const moreCardsSection = document.querySelector<HTMLDivElement>("#bottom-meldungen .more-content.row.section-1")
    if (!moreCardsSection) return

    announcementsContainer.style.transition = "height 0.5s"
    announcementsContainer.style.height = `${maxHeight}px`
    announcementsSection.classList.add("more-is-open")
    sessionStorage.setItem(announcementsStorageKey, "1")
}

const hideAnnouncements = () => {
    const announcementsSection = announcementsBottomNewsContainerElement()
    if (!announcementsSection) return

    const announcementsContainer = newsBottomRowMeldungen() as HTMLElement
    const card = announcementsSection.querySelector<HTMLDivElement>(".anzeige-meldung")
    const title = announcementsSection.querySelector<HTMLDivElement>(".stripe-title")

    if (!announcementsContainer || !card || !title) return

    const cardHeight = card.clientHeight
    const titleHeight = title.clientHeight

    let minHeight = cardHeight + cardHeight + titleHeight + gutter

    if (SiteInfo.isMobileVersion) {
        minHeight = 4 * cardHeight + titleHeight + gutter
    }

    const moreCardsSection = document.querySelector<HTMLDivElement>("#bottom-meldungen .more-content.row.section-1")
    if (!moreCardsSection) return

    announcementsContainer.style.transition = "height 0.5s"

    announcementsContainer.style.height = `${minHeight}px`
    scrollPageTo(announcementsSection)
    announcementsSection.classList.remove("more-is-open")
    sessionStorage.setItem(announcementsStorageKey, "0")
}

const toggleAnnouncements = (e: Event) => {
    e.preventDefault()
    const announcementsSection = announcementsBottomNewsContainerElement()
    if (!announcementsSection) return

    if (announcementsSection.classList.contains("more-is-open")) {
        if (e.currentTarget) {
            ;(e.currentTarget as HTMLAnchorElement).setAttribute("href", "/aktuelles?information-page=2")
        }
        hideAnnouncements()
        setUrlParamsToSpecificValue({ "information-page": null })
    } else {
        if (e.currentTarget) {
            ;(e.currentTarget as HTMLAnchorElement).setAttribute("href", "/aktuelles")
        }
        setUrlParamsToSpecificValue({ "information-page": "2" })
        showAnnouncements()
    }
}

async function fetchAnnouncementInformation() {
    const result = await fetch("/api/announcement/information", {
        method: "POST",
        credentials: "include",
        body: JSON.stringify({
            excludeTestData: false,
            firstAnnouncementCards: false,
            locationJsonEncoded: SiteInfo.locationObjectJsonEncoded,
            deviceType: SiteInfo.isMobileVersion ? "mobile" : "pc",
        }),
    })

    return await result.json()
}

function checkIfUrlInformationPageParameterIsSet() {
    const urlParams = new URLSearchParams(window.location.search)
    const informationPageUrlParam = urlParams.get("information-page")

    return informationPageUrlParam === "2"
}

export const initBottomAnnouncements = async () => {
    if (!announcementsSection || !announcementsButton || !announcementsContainer) return

    setTimeoutForCalculateAndSetHeights()

    if (checkIfUrlInformationPageParameterIsSet()) {
        calculateAndSetHeights()
        showAnnouncements()

        if (announcementsSection.classList.contains("more-is-open")) {
            announcementsButton.setAttribute("href", "/aktuelles")
        }
        sessionStorage.setItem(bottomAnnouncementRestLoadedStorageKey, "1")
    }
    if (
        !checkIfUrlInformationPageParameterIsSet() &&
        sessionStorage.getItem(announcementsStorageKey) === "1" &&
        sessionStorage.getItem(bottomAnnouncementRestLoadedStorageKey) === "1"
    ) {
        sessionStorage.removeItem(announcementsStorageKey)
        sessionStorage.removeItem(bottomAnnouncementRestLoadedStorageKey)
    }

    announcementsButton?.addEventListener("click", (event: MouseEvent) => {
        if (checkIfUrlInformationPageParameterIsSet()) {
            return
        }

        if (announcementsSection.classList.contains("more-is-open")) {
            ;(event.currentTarget as HTMLAnchorElement).setAttribute("href", "/aktuelles")
            return
        }

        if (
            sessionStorage.getItem(bottomAnnouncementRestLoadedStorageKey) === "1" &&
            containsNextPreviousId(announcementsInformationRestElement()?.innerHTML ?? "")
        )
            return

        fetchAnnouncementInformation().then((resultJsonRest) => {
            if (announcementsRest === null) return

            announcementsRest.innerHTML = resultJsonRest.announcementCardsRest
            sessionStorage.setItem(bottomAnnouncementRestLoadedStorageKey, "1")

            calculateAndSetHeights()
            return
        })
    })

    if (announcementsPreview === null) return

    announcementsContainer.style.overflow = "hidden"
    setTimeoutForCalculateAndSetHeights()

    announcementsButton.addEventListener("click", toggleAnnouncements)
    window.addEventListener("resize", setTimeoutForCalculateAndSetHeights)
}

function containsNextPreviousId(html: string): boolean {
    return html.includes(`data-next-previous-id="bottomAnnouncement`)
}
